import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const schema = JSON.stringify([
  {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Simple Meal Plan for Men Over 60 (Build Muscle, Lose Fat)",
    "author": { "@type": "Person", "name": "Mason Hale" },
    "datePublished": "2026-01-22",
    "dateModified": "2026-04-06",
    "publisher": { "@type": "Organization", "name": "Mason Hale Fitness" },
    "url": "https://masonhalefit.com/blog/meal-plan-for-men-over-60"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How many calories should a man over 60 eat to lose weight?",
        "acceptedAnswer": { "@type": "Answer", "text": "Most men over 60 lose weight effectively on 1,800-2,200 calories per day, depending on activity level and body size. A modest deficit of 300-500 calories below maintenance is sustainable and preserves muscle mass." }
      },
      {
        "@type": "Question",
        "name": "What should men over 60 eat to build muscle?",
        "acceptedAnswer": { "@type": "Answer", "text": "Prioritize protein at every meal (30-40g per sitting), include complex carbohydrates for energy, and don't fear healthy fats. Focus on whole foods: eggs, chicken, fish, Greek yogurt, oats, sweet potatoes, and vegetables." }
      },
      {
        "@type": "Question",
        "name": "Should men over 60 eat breakfast?",
        "acceptedAnswer": { "@type": "Answer", "text": "Yes, especially if you're strength training. A protein-rich breakfast (30g+) sets the tone for the day, reduces cravings, and supports muscle protein synthesis throughout the morning." }
      },
      {
        "@type": "Question",
        "name": "How many meals should men over 60 eat per day?",
        "acceptedAnswer": { "@type": "Answer", "text": "3-4 meals per day works well for most men over 60. This allows you to spread protein intake evenly (30-40g per meal) which is more effective for muscle building than eating most protein in one sitting." }
      }
    ]
  }
]);

const tocItems = [
  { id: 'principles', label: 'The 3 Nutrition Principles That Matter Most' },
  { id: 'calories', label: 'How Many Calories Do You Need?' },
  { id: 'sample-plan', label: '7-Day Sample Meal Plan' },
  { id: 'meal-prep', label: 'Simple Meal Prep Strategy' },
  { id: 'foods-to-avoid', label: 'Foods to Minimize After 60' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const related = [
  { slug: 'protein-intake-for-men-over-60', title: 'How Much Protein Do Men Over 60 Actually Need?', category: 'Nutrition' },
  { slug: 'best-diet-for-men-over-60-to-lose-weight', title: 'Best Diet for Men Over 60 to Lose Weight (Without Starving)', category: 'Weight Loss' },
  { slug: 'weight-loss-for-men-over-60', title: 'Weight Loss for Men Over 60: The No-Diet Approach', category: 'Weight Loss' },
];

export default function MealPlanPage() {
  return (
    <BlogLayout
      title="Meal Plan for Men Over 60: Build Muscle, Lose Fat"
      description="A simple, realistic meal plan for men over 60 to build muscle and lose fat. No complicated diets — just practical eating that actually works."
      keywords="meal plan for men over 60, diet plan for men over 60, what to eat over 60"
      canonicalPath="/blog/meal-plan-for-men-over-60"
      datePublished="2026-01-22"
      dateModified="2026-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Simple Meal Plan for Men Over 60 (Build Muscle, Lose Fat)" />

      <article>
        <header className="mb-8">
          <span className="text-[#c8a96e] text-xs font-bold tracking-[0.25em] uppercase">Nutrition</span>
          <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mt-2 mb-4">
            Simple Meal Plan for Men Over 60 (Build Muscle, Lose Fat)
          </h1>
          <p className="text-gray-400 text-base leading-relaxed">
            You don't need a complicated diet. You don't need to count every calorie or eliminate entire food groups. What you need is a simple, sustainable approach to eating that supports your training and your goals.
          </p>
          <p className="text-gray-400 text-base leading-relaxed mt-3">
            This meal plan for men over 60 is built around one core principle: enough protein to build muscle, enough calories to fuel your workouts, and enough flexibility to actually stick with it.
          </p>
        </header>

        <TableOfContents items={tocItems} />

        <section id="principles" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">The 3 Nutrition Principles That Matter Most</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            Forget the diet trends. These three principles cover 90% of what you need to know:
          </p>

          <div className="space-y-4 mb-6">
            {[
              { num: '1', title: 'Protein First', desc: 'Hit 0.7-1.0g of protein per pound of body weight every day. This is the single most important nutritional factor for building muscle and losing fat after 60. Everything else is secondary.' },
              { num: '2', title: 'Eat Mostly Whole Foods', desc: 'Lean meats, eggs, fish, vegetables, fruits, legumes, whole grains. Not because processed food is evil, but because whole foods are more filling, more nutritious, and make hitting your protein targets easier.' },
              { num: '3', title: 'Modest Calorie Deficit for Fat Loss', desc: 'If you want to lose fat, eat 300-500 calories below your maintenance level. This is enough to lose 0.5-1 lb per week without sacrificing muscle or energy.' },
            ].map(({ num, title, desc }) => (
              <div key={num} className="bg-[#111111] border border-white/10 rounded-lg p-5 flex gap-4">
                <span className="w-8 h-8 flex items-center justify-center bg-[#c8a96e] text-black font-black text-sm rounded-full flex-shrink-0 mt-0.5">{num}</span>
                <div>
                  <p className="text-white font-bold mb-1">{title}</p>
                  <p className="text-gray-400 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-400 leading-relaxed">
            According to <a href="https://www.health.harvard.edu/staying-healthy/how-much-protein-do-you-need-every-day" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">Harvard Health</a>, spreading protein intake evenly across meals is more effective for muscle building than eating most of it in one sitting — especially for older adults.
          </p>
        </section>

        <section id="calories" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">How Many Calories Do You Need?</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            A rough starting point for men over 60 who strength train 3x per week:
          </p>

          <div className="bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6 mb-6">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-[#c8a96e] font-bold py-2 pr-4">Goal</th>
                    <th className="text-left text-gray-400 font-semibold py-2">Daily Calories</th>
                  </tr>
                </thead>
                <tbody className="text-gray-400">
                  {[
                    ['Lose fat (preserve muscle)', '1,800-2,100 calories'],
                    ['Maintain weight', '2,100-2,400 calories'],
                    ['Build muscle (lean bulk)', '2,400-2,700 calories'],
                  ].map(([goal, cals]) => (
                    <tr key={goal} className="border-b border-white/5">
                      <td className="py-2 pr-4 text-white font-semibold">{goal}</td>
                      <td className="py-2">{cals}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-gray-500 text-xs mt-3">These are estimates. Adjust based on your results over 2-3 weeks.</p>
          </div>
        </section>

        <section id="sample-plan" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">7-Day Sample Meal Plan</h2>
          <p className="text-gray-400 leading-relaxed mb-4">
            This plan targets approximately 160g protein and 2,000 calories per day — suitable for a 190-pound man aiming to lose fat while preserving muscle.
          </p>

          <div className="space-y-4 mb-6">
            {[
              {
                day: 'Monday / Thursday',
                meals: [
                  { time: 'Breakfast', food: '4 scrambled eggs + 1 cup oatmeal with berries', macros: '~45g protein, 450 cal' },
                  { time: 'Lunch', food: '6oz grilled chicken + large salad + olive oil dressing', macros: '~42g protein, 480 cal' },
                  { time: 'Snack', food: '1 cup Greek yogurt + handful of almonds', macros: '~20g protein, 280 cal' },
                  { time: 'Dinner', food: '6oz salmon + roasted vegetables + 1/2 cup brown rice', macros: '~42g protein, 520 cal' },
                ]
              },
              {
                day: 'Tuesday / Friday',
                meals: [
                  { time: 'Breakfast', food: '3 eggs + 2 turkey sausage links + 1 slice whole grain toast', macros: '~38g protein, 420 cal' },
                  { time: 'Lunch', food: '1 can tuna + 1/2 avocado + whole grain crackers', macros: '~35g protein, 450 cal' },
                  { time: 'Snack', food: '1 cup cottage cheese + sliced cucumber', macros: '~25g protein, 200 cal' },
                  { time: 'Dinner', food: '6oz lean ground beef + zucchini noodles + marinara', macros: '~42g protein, 480 cal' },
                ]
              },
              {
                day: 'Wednesday / Saturday',
                meals: [
                  { time: 'Breakfast', food: 'Protein smoothie: 1 scoop whey + banana + spinach + almond milk', macros: '~30g protein, 380 cal' },
                  { time: 'Lunch', food: 'Turkey and cheese wrap + side salad', macros: '~38g protein, 460 cal' },
                  { time: 'Snack', food: '2 hard-boiled eggs + apple', macros: '~13g protein, 220 cal' },
                  { time: 'Dinner', food: '6oz chicken thighs + sweet potato + steamed broccoli', macros: '~42g protein, 520 cal' },
                ]
              },
            ].map(({ day, meals }) => (
              <div key={day} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                <p className="text-[#c8a96e] font-bold text-sm uppercase tracking-wide mb-3">{day}</p>
                <div className="space-y-3">
                  {meals.map(({ time, food, macros }) => (
                    <div key={time} className="flex items-start gap-3 border-b border-white/5 pb-2">
                      <span className="text-white text-xs font-bold w-20 flex-shrink-0 pt-0.5">{time}</span>
                      <div>
                        <p className="text-gray-400 text-sm">{food}</p>
                        <p className="text-gray-600 text-xs mt-0.5">{macros}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-gray-400 leading-relaxed">
            Sunday is flexible — eat similar foods in similar amounts. The goal is consistency, not perfection.
          </p>
        </section>

        <section id="meal-prep" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Simple Meal Prep Strategy</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            You don't need to spend Sunday cooking for 4 hours. Here's a 90-minute prep that sets you up for the week:
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li>Grill or bake 3-4 lbs of chicken breast — use throughout the week</li>
            <li>Hard-boil a dozen eggs — grab and go protein all week</li>
            <li>Cook a big batch of rice or oats</li>
            <li>Wash and chop vegetables for easy salads</li>
            <li>Portion out Greek yogurt and cottage cheese into individual containers</li>
          </ul>
          <p className="text-gray-400 leading-relaxed">
            The <a href="https://www.mayoclinic.org/healthy-lifestyle/nutrition-and-healthy-eating/in-depth/healthy-recipes/art-20047195" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:underline">Mayo Clinic</a> recommends meal planning as one of the most effective strategies for maintaining a healthy diet long-term.
          </p>
        </section>

        <section id="foods-to-avoid" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-4">Foods to Minimize After 60</h2>
          <p className="text-gray-400 leading-relaxed mb-3">
            You don't need to eliminate anything completely. But these foods work against your goals:
          </p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 mb-4 ml-2">
            <li><strong className="text-white">Alcohol:</strong> Directly impairs muscle protein synthesis and sleep quality. If you drink, keep it to 1-2 drinks max, not nightly.</li>
            <li><strong className="text-white">Ultra-processed foods:</strong> High in calories, low in protein and nutrients. They crowd out the foods that actually help you.</li>
            <li><strong className="text-white">Sugary drinks:</strong> Liquid calories with no satiety. Swap for water, black coffee, or unsweetened tea.</li>
            <li><strong className="text-white">Excessive sodium:</strong> Increases blood pressure and water retention. Watch processed meats and canned soups.</li>
          </ul>
          <p className="text-gray-400 leading-relaxed">
            Want the complete nutrition system that pairs with a structured training program? The <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:underline font-semibold">Over-60 Strength Blueprint</a> includes detailed nutrition guidance alongside the workout program — so everything works together.
          </p>
        </section>

        <section id="faq" className="mt-10">
          <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {[
              { q: "How many calories should a man over 60 eat to lose weight?", a: "Most men over 60 lose weight effectively on 1,800-2,200 calories per day, depending on activity level and body size. A modest deficit of 300-500 calories below maintenance is sustainable and preserves muscle mass." },
              { q: "What should men over 60 eat to build muscle?", a: "Prioritize protein at every meal (30-40g per sitting), include complex carbohydrates for energy, and don't fear healthy fats. Focus on whole foods: eggs, chicken, fish, Greek yogurt, oats, sweet potatoes, and vegetables." },
              { q: "Should men over 60 eat breakfast?", a: "Yes, especially if you're strength training. A protein-rich breakfast (30g+) sets the tone for the day, reduces cravings, and supports muscle protein synthesis throughout the morning." },
              { q: "How many meals should men over 60 eat per day?", a: "3-4 meals per day works well for most men over 60. This allows you to spread protein intake evenly (30-40g per meal) which is more effective for muscle building than eating most protein in one sitting." },
            ].map(({ q, a }) => (
              <div key={q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
                <p className="text-white font-bold mb-2">{q}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-10 bg-[#111111] border border-[#c8a96e]/20 rounded-lg p-6">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-2">Eat Right, Train Right</p>
          <p className="text-white font-bold text-lg mb-2">Get The Over-60 Strength Blueprint</p>
          <p className="text-gray-400 text-sm mb-4">Complete nutrition guidance + a joint-safe strength training program designed for men over 60. $19.99 one-time.</p>
          <a href="https://masonhalefit.com" className="inline-flex items-center gap-2 px-6 py-3 bg-[#c8a96e] text-black text-sm font-black tracking-[0.15em] uppercase rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
            Get The Program — $19.99
          </a>
        </div>

        <AuthorBio />
        <RelatedArticles articles={related} />
      </article>
    </BlogLayout>
  );
}
